/**
 * WorldLoader — polls the server for an in-flight world generation job and
 * fires callbacks as each stage completes.
 *
 * Two providers, two response shapes (discriminated by `provider`):
 *
 *   Blockade Labs (Skybox AI) — two stages:
 *     Stage 1 (~20s): panorama ready → onSkyboxPanoReady(panoAssets)
 *     Stage 2 (~30-60s after): mesh ready → onSkyboxMeshReady(pano, mesh)
 *
 *   WorldLabs (Marble) — single stage (~5 min):
 *     splat ready → onWorldLabsReady(assets)
 *
 * onProgress fires with a human-readable description on every poll where the
 * server is still working — drives the loading badge text in the UI.
 *
 * After the final stage the loader POSTs the asset URLs to /world-cache so
 * the server downloads and caches them locally, then fires onCached so the
 * dropdown can reflect the new entry.
 */

const POLL_INTERVAL_MS = 5_000;
const FIRST_POLL_DELAY_MS = 5_000;

export interface SkyboxPanoAssets {
  worldId: string;
  panoUrl: string;
  thumbnailUrl: string;
  depthMapUrl: string;
}

export interface SkyboxMeshAssets {
  glbUrl: string;
}

export interface WorldLabsAssets {
  worldId: string;
  panoUrl: string;
  thumbnailUrl: string;
  splatUrl100k: string;
  splatUrl500k: string;
}

export interface CachedEntry {
  id: string;
  prompt: string;
  splatLocalUrl?: string;
  panoLocalUrl?: string;
  glbLocalUrl?: string;
}

interface BlockadePollResponse {
  provider: 'blockade';
  done: boolean;
  panoReady: boolean;
  panoAssets?: SkyboxPanoAssets;
  meshReady: boolean;
  meshAssets?: SkyboxMeshAssets;
  progress?: string;
  error?: string;
}

interface WorldLabsPollResponse {
  provider: 'worldlabs';
  done: boolean;
  progress?: string;
  assets?: WorldLabsAssets;
  error?: string;
}

type PollResponse = BlockadePollResponse | WorldLabsPollResponse | { error: string };

export class WorldLoader {
  private serverUrl: string;
  private timer: ReturnType<typeof setInterval> | null = null;
  private stopped = false;
  private currentPrompt = '';
  /** Tracks whether the Skybox panorama callback has already fired so we
   *  don't re-fire on every subsequent poll while waiting for the mesh. */
  private panoFired = false;

  onProgress?: (description: string) => void;

  /** Fires the moment the Blockade panorama is generated (Stage 1 complete).
   *  The mesh may still be building, OR may be skipped entirely if the API
   *  tier doesn't allow the export endpoint — in which case onSkyboxComplete
   *  will fire shortly after with mesh=null. */
  onSkyboxPanoReady?: (assets: SkyboxPanoAssets) => void;

  /** Fires when the Skybox operation is fully complete. mesh is null when
   *  the GLB export was unavailable (e.g. tier restriction) — the client
   *  should treat this as a normal pano-only completion, not an error. */
  onSkyboxComplete?: (pano: SkyboxPanoAssets, mesh: SkyboxMeshAssets | null) => void;

  /** Fires when a WorldLabs splat is ready. */
  onWorldLabsReady?: (assets: WorldLabsAssets) => void;

  /** Fires after the server has saved the assets locally — used to refresh
   *  the cached-environment dropdown. */
  onCached?: (entry: CachedEntry) => void;

  onError?: (message: string) => void;

  constructor(serverUrl: string) {
    this.serverUrl = serverUrl;
  }

  start(operationId: string, prompt = ''): void {
    // Defensive: clear any prior poll timer so stale intervals from a previous
    // operation can't keep firing alongside the new one.
    this.stop();
    this.stopped = false;
    this.currentPrompt = prompt;
    this.panoFired = false;
    console.log(`[world] polling for operation ${operationId}`);

    this.timer = setInterval(() => {
      void this.poll(operationId);
    }, POLL_INTERVAL_MS);

    setTimeout(() => {
      if (!this.stopped) void this.poll(operationId);
    }, FIRST_POLL_DELAY_MS);
  }

  stop(): void {
    this.stopped = true;
    if (this.timer !== null) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  private async poll(operationId: string): Promise<void> {
    if (this.stopped) return;

    let data: PollResponse;
    try {
      const res = await fetch(`${this.serverUrl}/world-status/${operationId}`);
      if (!res.ok) {
        console.warn(`[world] poll returned ${res.status}`);
        return;
      }
      data = (await res.json()) as PollResponse;
    } catch (err) {
      console.warn('[world] poll failed:', err);
      return;
    }

    if ('error' in data && data.error) {
      this.stop();
      console.warn('[world] generation error:', data.error);
      this.onError?.(data.error);
      return;
    }

    if (!('provider' in data)) {
      console.warn('[world] unrecognised poll response (no provider tag)');
      return;
    }

    if (data.provider === 'blockade') {
      this.handleBlockadePoll(data);
    } else {
      this.handleWorldLabsPoll(data);
    }
  }

  private handleBlockadePoll(data: BlockadePollResponse): void {
    // Fire pano-ready exactly once, the first poll where panoAssets appears.
    if (data.panoReady && data.panoAssets && !this.panoFired) {
      this.panoFired = true;
      console.log('[world] skybox pano ready');
      this.onSkyboxPanoReady?.(data.panoAssets);
    }

    if (!data.done) {
      this.onProgress?.(data.progress ?? 'Generating environment…');
      return;
    }

    if (!data.panoAssets) {
      console.warn('[world] skybox done but pano assets missing');
      return;
    }

    this.stop();
    const mesh = data.meshAssets ?? null;
    console.log(
      `[world] skybox complete ${data.panoAssets.worldId} (${mesh ? 'with mesh' : 'pano-only'})`,
    );
    this.onSkyboxComplete?.(data.panoAssets, mesh);

    void this.cacheSkybox(data.panoAssets, mesh);
  }

  private handleWorldLabsPoll(data: WorldLabsPollResponse): void {
    if (!data.done) {
      console.log(`[world] worldlabs still generating: ${data.progress ?? '…'}`);
      this.onProgress?.(data.progress ?? 'Generating environment…');
      return;
    }

    this.stop();
    if (!data.assets) {
      console.warn('[world] worldlabs done but assets missing');
      return;
    }
    console.log('[world] worldlabs ready', data.assets.worldId);
    this.onWorldLabsReady?.(data.assets);

    void this.cacheWorldLabs(data.assets);
  }

  private async cacheSkybox(pano: SkyboxPanoAssets, mesh: SkyboxMeshAssets | null): Promise<void> {
    try {
      const res = await fetch(`${this.serverUrl}/world-cache`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: pano.worldId,
          panoUrl: pano.panoUrl,
          glbUrl: mesh?.glbUrl,
          prompt: this.currentPrompt || pano.worldId,
          thumbnailUrl: pano.thumbnailUrl,
        }),
      });
      if (!res.ok) {
        const text = await res.text().catch(() => res.statusText);
        console.warn(`[world] skybox cache POST failed ${res.status}: ${text.slice(0, 200)}`);
        return;
      }
      const entry = (await res.json()) as CachedEntry;
      console.log('[world] skybox saved to server cache');
      this.onCached?.(entry);
    } catch (err) {
      console.warn('[world] failed to cache skybox:', err);
    }
  }

  private async cacheWorldLabs(assets: WorldLabsAssets): Promise<void> {
    try {
      const res = await fetch(`${this.serverUrl}/world-cache`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: assets.worldId,
          splatUrl: assets.splatUrl500k,
          prompt: this.currentPrompt || assets.worldId,
          thumbnailUrl: assets.thumbnailUrl,
        }),
      });
      if (!res.ok) {
        const text = await res.text().catch(() => res.statusText);
        console.warn(`[world] worldlabs cache POST failed ${res.status}: ${text.slice(0, 200)}`);
        return;
      }
      const entry = (await res.json()) as CachedEntry;
      console.log('[world] worldlabs saved to server cache');
      this.onCached?.(entry);
    } catch (err) {
      console.warn('[world] failed to cache worldlabs splat:', err);
    }
  }
}
